import { useDisplayCurrency } from './currency/DisplayCurrencyProvider';
import { ticketsRound, ticketUnitDisplay, ticketDisplayTotal, formatAmountLabel } from './Util/formatUtils';
import React, { useRef } from 'react';
import FormUnavailable from './Components/FormUnavailable';
import { useEffect, useState } from 'react';
import { usePostHog } from 'posthog-js/react';
import { MinusIcon, PlusIcon } from '@heroicons/react/24/outline';
import { ensureLostCheckoutTapListener } from './Util/checkoutAckWatcher';
import { fieldMapper } from './Util/FieldMapper.jsx';
import { AnimatedError, LEGACY_UNBRANDED_FORM_COLOR, STEPPER_COUNT_CLASS, STEPPER_GROUP_CLASS, STEPPER_ICON_CLASS, stepperButtonClass, TILE_SELECTED_COLOR_CLASS, JOURNEY_MARKS, Sentry, markJourney, prepareColorsForPostMessage, safePostMessageToParent, safePostMessageWithSelfFallback, useBrandColors } from '@charitystack/shared';
import GiftSummaryRow from './Components/GiftSummaryRow';
import {
  attachDonateSnapshot,
  displayFromUsd,
  markTicketPricesRounded,
  snappedUnitsTotal,
  snapshotOf,
  stripConfigOnlyKeys,
} from './currency/withDisplaySnapshot';
import { isDevelopment, getBackendUrl, buildApiUrl } from './config/env';
import { useIsHostedPage } from './hooks/useIsHostedPage';
import { navigateAfterReset } from './Util/formNavigation';
import { scheduleFormReset, useFormResetTimeouts } from './Util/formResetUtils';
import {
  addBuyerTicketUnit,
  applyBuyerTicketPrefill,
  getBuyerTicketAvailability,
  projectBuyerTicketPurchaseSafely,
  refreshBuyerTicketsFromConfiguration,
  removeBuyerTicketUnit,
  resizeBuyerTicketGroup,
  TICKET_AVAILABILITY_CHANGED_MESSAGE,
} from './Util/groupTicketBuyerState';

import {
  formCardClass,
  getStandardizedFormBodyStyles,
  getStandardizedFormElementStyles,
  getStandardizedFormHeight,
} from './Util/formHeightUtils';
import { getPageTransitionClasses } from './Util/pageTransition';
import FormActionButtons from './Components/FormActionButtons';
import FormTitleStrip from './Components/FormTitleStrip';
import { getDetailPageBack } from './Util/detailPageBack';
// PostHog form analytics
import { trackForm, FORM_EVENTS, FORM_TYPES, registerFormContext } from '@charitystack/shared';

const generateConfirmationID = () => {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  return Array.from(
    { length: 7 },
    () => characters[Math.floor(Math.random() * characters.length)],
  ).join('');
};

const generatePurchasedGroupID = () => {
  if (typeof globalThis.crypto?.randomUUID === 'function') {
    return globalThis.crypto.randomUUID();
  }
  const bytes = new Uint8Array(16);
  globalThis.crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

function TicketSelectionForm(props) {
  // Extract setPage prop for parent navigation (back button)
  const {
    setPage: setParentPage,
    transitionToPage,
    currentPage: parentCurrentPage,
    hostedPagePreview = false,
    enableEmbedForm = false,
    formDisplayMode = 'dynamic',
    isPageActive = false,
    pageTransitionDirection = null,
  } = props;
  const isHostedPage = useIsHostedPage();
  const posthog = usePostHog();

  // UNIFIED LOADING APPROACH:
  // - The loading frame is FormSkeleton's event frame (Components/skeleton)
  // - Real-time updates (5-second polling) work independently of loading states
  // - Simple and maintainable loading experience

  const ctx = useDisplayCurrency();
  const formatAmount = (value) => {
    const n = Number(value);
    if (Number.isNaN(n)) return value;
    return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };
  // write a function that takes a string timestamp formatted as 2023-08-02 04:37:29.897884
  // and converts it to a format like April 30th, 2023 at 7:52pm in the user's local timezone
  function formatTimestamp(timestamp) {
    const date = new Date(timestamp);
    const timezoneOffsetInMinutes = date.getTimezoneOffset();

    // Apply the timezone offset to the date
    date.setMinutes(date.getMinutes() - timezoneOffsetInMinutes);

    const options = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
    };

    const formattedDate = date.toLocaleString(undefined, options);
    return formattedDate;
  }
  const [hostedPage, setHostedPage] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Individual error states
  const [ticketSelectionError, setTicketSelectionError] = useState('');
  const [configData, setConfigData] = useState({});
  const rounded = ticketsRound(configData);
  const [consecutiveFailures, setConsecutiveFailures] = useState(0);

  // Remove local active state - will use configData.active instead
  const [elementID, setElementID] = useState('');
  const [elementName, setElementName] = useState('');
  const [tickets, setTickets] = useState([]);
  const [customInputsValues, setCustomInputsValues] = useState([]);
  const [customInputsAttendee, setCustomInputsAttendee] = useState([]);
  // NEW: Use shared brand colors hook
  // NOTE: element_config call has primaryColor defined as lightColor, therefore we take configData?.lightColor from the payload
  // CRITICAL: Use props.data?.lightColor as primary source since configData may be stale
  // CON-528: no colour is the no-brand set (the legacy "no colour" value), never sage.
  const colorForBrandColors =
    props.data?.lightColor || configData?.lightColor || LEGACY_UNBRANDED_FORM_COLOR;

  // CRITICAL FIX: Use pre-generated brand colors from parent (EmbedForm) if available
  // This prevents flicker caused by useBrandColors initializing with empty state
  const {
    brandColors: localBrandColors,
    primaryColor: localPrimaryColor,
    darkColor: localDarkColor,
  } = useBrandColors(colorForBrandColors);

  // Use parent's pre-generated colors if available, otherwise fall back to local generation
  const hasParentBrandColors =
    props.generatedBrandColors && Object.keys(props.generatedBrandColors).length > 0;
  const brandColors = hasParentBrandColors ? props.generatedBrandColors : localBrandColors;
  const primaryColor = hasParentBrandColors
    ? props.generatedBrandColors['accent-50'] || localPrimaryColor
    : localPrimaryColor;
  const darkColor = hasParentBrandColors
    ? props.generatedBrandColors['accent-60'] || localDarkColor
    : localDarkColor;

  const [page, setPage] = useState('START_PAGE');

  // Page transition function - CSS handles the animation automatically
  const smoothSetPage = (targetPage) => {
    setPage(targetPage);
  };

  // Enhanced page transition function for callback-style setPage
  const smoothSetPageCallback = (callback) => {
    if (transitionToPage && typeof transitionToPage === 'function') {
      // For functional updates, we need to handle differently
      const targetPage = callback(page);
      transitionToPage(targetPage, setPage, page);
    } else {
      // Fallback to direct page change
      setPage(callback);
    }
  };
  const [ticketTransactions, setTicketTransactions] = useState(new Map());
  const [totalTickets, setTotalTickets] = useState(0);
  const [total, setTotal] = useState(0);

  const [ticket2, setTicket2] = useState({ ticket: [] });
  const [allAttendees, setAllAttendees] = useState([]); // this is the list of all attendees from all tickets
  const [map, setMapping] = useState({}); // this is the list of all attendees from all tickets
  const [amt, setAmt] = useState(0); // this is the list of all attendees from all tickets
  const [currentIndex, setCurrentIndex] = useState(0);

  const [domID, setDomID] = useState('');

  // Color generation now handled by useBrandColors hook

  // Color preservation now handled by useBrandColors hook

  useEffect(() => {
    setIsDrawerOpen(props.isDrawerOpen);
  }, [props.isDrawerOpen]);

  const listener = (event) => {
    if (!(event.data.entity == 'CHARITYSTACK' && event.data.action == 'RESET_ELEMENT')) return;

    setIsSubmitting(false);
    smoothSetPage('START_PAGE');
    setTicketTransactions(new Map());

    // `forceApply: true` like the standard, crowdfunding and product resets.
    // Without it an identical payload takes `fetchElementsConfig`'s equality
    // shortcut and the form keeps its previous `data` identity, so selections
    // seeded from `data` survive a reset that is supposed to restore the
    // configured defaults.
    props.fetchElementsConfig({ forceApply: true }).catch((err) => console.log('ERROR', err));
  };

  const goBackListener = (event) => {
    if (!(event.data.entity == 'CHARITYSTACK' && event.data.action == 'GO_BACK')) return;

    setIsSubmitting(false);
    // Simplified logic since we're no longer using attendee pages
    smoothSetPageCallback((currentPage) => {
      if (currentPage === 'START_PAGE' || currentPage === 'CUSTOM_INPUTS_PAGE') {
        return 'START_PAGE';
      }
      return 'START_PAGE';
    });
  };

  useEffect(() => {
    if (hostedPage !== null) {
      // send message that element has finished loading
      safePostMessageToParent(
        {
          action: 'FINISHED_LOADING',
          entity: 'CHARITYSTACK',
          sender: 'EVENT',
          receiver: 'HOSTED_PAGE',
        },
        '*',
      );
    }
  }, [hostedPage]);

  useEffect(() => {}, [page]);

  useEffect(() => {
    window.addEventListener('message', listener);
    window.addEventListener('message', goBackListener);
    // grab hostedPage boolean
    const urlParams = new URLSearchParams(window.location.search);
    const hostedPage = urlParams.get('page');
    // hostedPage: null (embedded) or truthy value (hosted page)
    setHostedPage(hostedPage);
    setDomID(urlParams.get('domID'));
    return () => {
      window.removeEventListener('message', listener);
      window.removeEventListener('message', goBackListener);
    };
  }, []);

  // HYBRID APPROACH: Use props.data for initial setup (like other forms)
  const ticketsInitializedRef = useRef(false);
  useEffect(() => {
    const { data } = props;

    if (data) {
      // Process data like other forms
      setConfigData(data);
      setElementID(data.elementID);
      setElementName(data.elementName);

      // Add defensive checks for arrays before calling .map()
      if (data.customInputs && Array.isArray(data.customInputs)) {
        data.customInputs.forEach((input) => {
          input.answers = [''];
        });
      }

      if (data.tickets && Array.isArray(data.tickets)) {
        // Dashboard previews are authoring surfaces: configuration edits must replace
        // the displayed ticket definitions immediately. Live buyer surfaces preserve
        // selections and refresh only availability after their initial load.
        if (!ticketsInitializedRef.current || props.previewMode) {
          const reconciliation = refreshBuyerTicketsFromConfiguration({
            existingTickets: ticket2.ticket,
            freshTickets: data.tickets,
            ticketContractVersion: data.ticketContractVersion,
            initialized: ticketsInitializedRef.current,
            previewMode: props.previewMode,
          });
          setTickets(reconciliation.tickets);
          setTicket2({ ticket: reconciliation.tickets });
          if (reconciliation.resetSelection) {
            setIsSubmitting(false);
            setTicketTransactions(new Map());
            setTotalTickets(0);
            setTotal(0);
            setAllAttendees([]);
            setMapping({});
            setAmt(0);
            setCurrentIndex(0);
            setTicketSelectionError('');
            smoothSetPage('START_PAGE');
          }
          ticketsInitializedRef.current = true;
        } else {
          // Subsequent updates: only update availability (quantity/sold) without resetting user selections
          setTicket2((prev) => {
            const reconciliation = refreshBuyerTicketsFromConfiguration({
              existingTickets: prev.ticket,
              freshTickets: data.tickets,
              ticketContractVersion: data.ticketContractVersion,
              initialized: true,
              previewMode: false,
            });
            return { ticket: reconciliation.tickets };
          });
        }
      }

      // Color updates are now handled automatically by useBrandColors hook when configData.lightColor changes

      // Add defensive checks for customInputs filtering
      if (data.customInputs && Array.isArray(data.customInputs)) {
        const customInputPurchaser = data.customInputs.filter(
          (question) => question.questionType === 'PURCHASER',
        );

        const customInputAttendee = data.customInputs.filter(
          (question) => question.questionType === 'ATTENDEE',
        );

        setCustomInputsValues(customInputPurchaser);
        setCustomInputsAttendee(customInputAttendee);
      }
    }
  }, [props.data]);

  const createAttendeeFactory = (selectedTicket, urlOverrides = {}) => {
    let nextSeatNumber = (selectedTicket.attendees ?? []).length + 1;
    return ({ ticket, type, seats, seatIndex, purchasedGroupID }) => {
      const seatKey = `seat${nextSeatNumber}_`;
      nextSeatNumber += 1;
      const strip = (value) => value.toLowerCase().replace(/[^a-z0-9_]/g, '');
      const attendeeInputs = customInputsAttendee.map((input) => {
        const emptyInput = {
          ...input,
          answers: input.answers?.some((answer) => answer && answer.trim() !== '')
            ? input.answers
            : [],
        };
        if (!input.title) return emptyInput;
        const expectedKey = `${seatKey}ci_${strip(input.title.replace(/\s+/g, '_'))}`;
        const match = Object.entries(urlOverrides).find(([key]) => strip(key) === expectedKey);
        if (!match) return emptyInput;
        return {
          ...emptyInput,
          answers: input.type === 'MULTI_SELECTION_DROPDOWN' ? match[1].split(',') : [match[1]],
        };
      });
      return {
        checkInStatus: false,
        ticketName: ticket.name,
        ticketID: ticket.id,
        type,
        price: ticket.price,
        firstName: urlOverrides[`${seatKey}firstname`] || '',
        lastName: urlOverrides[`${seatKey}lastname`] || '',
        email: urlOverrides[`${seatKey}email`] || '',
        confirmationID: generateConfirmationID(),
        ...(type === 'GROUP'
          ? { seats, purchasedGroupID, seatIndex }
          : configData.ticketContractVersion === 2
            ? {}
            : { seats: 1 }),
        customInputAttendee: attendeeInputs,
      };
    };
  };

  const applyTicketSelections = (nextTickets) => {
    setTicket2({ ticket: nextTickets });
    setAmt(nextTickets.reduce((sum, ticket) => sum + (Number(ticket.soldNowPrice) || 0), 0));
    setAllAttendees(nextTickets.flatMap((ticket) => ticket.attendees ?? []));
  };

  // Auto-select ticket from URL param (e.g., ?ticket=Ticket%20with%20price)
  const ticketAutoSelectedRef = useRef(false);
  useEffect(() => {
    const urlOverrides = props.urlOverrides || {};
    const ticketParam = urlOverrides.ticket;

    // Only run once, when tickets are initialized and ticket param exists
    if (!ticketParam || ticketAutoSelectedRef.current) return;
    if (!ticket2.ticket || ticket2.ticket.length === 0) return;

    // Find matching ticket by name: exact first (the public API validates the
    // seat count against the exact name), then case-insensitive for hand-typed URLs.
    const exactIndex = ticket2.ticket.findIndex((t) => t.name === ticketParam);
    const ticketIndex =
      exactIndex !== -1
        ? exactIndex
        : ticket2.ticket.findIndex(
            (t) => t.name && t.name.toLowerCase() === ticketParam.toLowerCase(),
          );
    if (ticketIndex === -1) return;

    ticketAutoSelectedRef.current = true;

    const newTickets = [...ticket2.ticket];
    const targetTicket = newTickets[ticketIndex];
    const purchasedGroupID =
      configData.ticketContractVersion === 2 ? generatePurchasedGroupID() : undefined;
    const prefillResult = applyBuyerTicketPrefill({
      ticket: targetTicket,
      ticketContractVersion: configData.ticketContractVersion,
      purchasedGroupID,
      requestedSeatCount: urlOverrides.ticketseats,
      createAttendee: createAttendeeFactory(targetTicket, urlOverrides),
    });
    const updatedTicket = prefillResult.ticket;
    if (updatedTicket === targetTicket) {
      setTicketSelectionError(prefillResult.error || 'This ticket is no longer available');
      return;
    }
    newTickets[ticketIndex] = updatedTicket;
    applyTicketSelections(newTickets);
    setTicketSelectionError(prefillResult.error || '');
  }, [ticket2.ticket, props.urlOverrides, customInputsAttendee, configData.ticketContractVersion]);

  // Track form:viewed when configData is loaded
  const hasTrackedFormView = useRef(false);
  useEffect(() => {
    if (configData?.formID && !hasTrackedFormView.current) {
      hasTrackedFormView.current = true;

      registerFormContext({
        organizationId: configData.organizationId,
        formId: configData.formID,
        formName: configData.formName,
        merchantName: configData.nonprofitName,
        formType: FORM_TYPES.TICKET,
        elementId: elementID,
      });

      // CRITICAL: Wrap in try-catch to ensure analytics never blocks user flow
      try {
        trackForm(FORM_EVENTS.FORM_VIEWED, {
          form_id: configData.formID,
          form_type: FORM_TYPES.TICKET,
          form_name: configData.formName,
          merchant_name: configData.nonprofitName,
          is_hosted_page: isHostedPage,
          tickets_count: tickets?.length || 0,
        });
      } catch (error) {
        console.error('[TicketSelectionForm] Analytics tracking failed (non-blocking):', error);
      }
    }
  }, [configData?.formID, elementID, isHostedPage, tickets?.length]);

  const hasErrors = () => {
    let hasError = false;

    // Clear previous errors first
    setTicketSelectionError('');

    if (allAttendees.length == 0) {
      setTicketSelectionError('Please select at least one ticket');
      hasError = true;
    }

    const projection = projectBuyerTicketPurchaseSafely({
      ticketContractVersion: configData.ticketContractVersion,
      tickets: ticket2.ticket,
    });
    if (!projection.purchase) {
      setTicketSelectionError(projection.error);
      hasError = true;
    }

    return hasError;
  };

  const handleButtonClick = () => {
    if (hasErrors()) {
      return;
    }
    // Directly trigger the purchase without going through additional pages
    handlePurchase();
  };

  // Function to clear ticket selection error when users interact with tickets
  const clearTicketSelectionError = () => {
    if (allAttendees.length > 0) {
      setTicketSelectionError('');
    }
  };

  // Reset form state after checkout (called via setTimeout after DONATE)
  // The post-DONATE reset timer is cleared on unmount (see useFormResetTimeouts).
  const keepResetTimeoutId = useFormResetTimeouts();

  const resetFormState = () => {
    setIsSubmitting(false);
    // Reset ticket selections
    setTicketTransactions(new Map());
    setAllAttendees([]);
    setAmt(0);
    setMapping({});

    // Reset tickets data - clear attendees for all tickets
    if (ticket2.ticket && Array.isArray(ticket2.ticket)) {
      const resetTickets = ticket2.ticket.map((t) => ({
        ...t,
        soldNow: 0,
        soldNowPrice: 0,
        attendees: [],
        ...(configData.ticketContractVersion === 2 ? { selectedGroups: [] } : {}),
      }));
      setTicket2({ ticket: resetTickets });
    }

    // Clear errors
    setTicketSelectionError('');

    // Reset initialization refs so next props.data load re-initializes
    ticketsInitializedRef.current = false;
    ticketAutoSelectedRef.current = false;

    // Reset to START_PAGE
    smoothSetPage('START_PAGE');

    // Navigate to correct parent page using utility
    navigateAfterReset({
      setParentPage,
      isHostedPage,
      enableEmbedForm,
      formDisplayMode,
      isDrawerOpen: props.isDrawerOpen,
    });
  };

  const handlePurchase = () => {
    if (isSubmitting || props.previewMode || hostedPagePreview || (props.data?.paymentsEnabled === false && amt > 0)) return;

    // 🔧 FIX: Create deep copy to avoid mutating React state (configData)
    // This ensures each ticket purchase starts with a clean config copy
    const data = stripConfigOnlyKeys(JSON.parse(JSON.stringify(configData)));
    data.customInputsValues = customInputsValues;

    const projection = projectBuyerTicketPurchaseSafely({
      ticketContractVersion: configData.ticketContractVersion,
      tickets: ticket2.ticket,
    });
    if (!projection.purchase) {
      setTicketSelectionError(projection.error);
      return;
    }
    const purchase = projection.purchase;
    data.tickets = purchase.tickets;
    data.amount = purchase.amount;
    data.ticketMapping = purchase.ticketMapping;
    data.ticketDisplayItems = purchase.displayLineItems;
    if (purchase.ticketContractVersion === 2) {
      data.ticketContractVersion = 2;
      data.purchasedGroups = purchase.purchasedGroups;
    } else {
      delete data.ticketContractVersion;
      delete data.purchasedGroups;
    }
    data.frequency = 'ONE_TIME';
    data.fund = '';

    let donateSnapshot = null;
    let displayGiftAmount = null;
    try {
      donateSnapshot = data.amount === 0 ? null : snapshotOf(ctx);
      if (donateSnapshot) {
        if (rounded) {
          const roundedTotal = snappedUnitsTotal(
            data.ticketDisplayItems.map((item) => {
              const ticket = ticket2.ticket.find((candidate) => candidate.id === item.ticketID);
              const isPerSeatGroup =
                item.type === 'GROUP' && ticket?.pricingMode === 'PER_SEAT';
              return {
                unitUsd: isPerSeatGroup ? ticket.price : item.amount,
                quantity: isPerSeatGroup ? item.seatCount : item.quantity,
              };
            }),
            donateSnapshot,
          );
          data.amount = roundedTotal.usd;
          displayGiftAmount = roundedTotal.display;
          if (data.amount !== 0) markTicketPricesRounded(data);
        } else {
          displayGiftAmount = displayFromUsd(data.amount, donateSnapshot);
        }
      }
    } catch (error) {
      setTicketSelectionError('Something went wrong. Please refresh and try again.');
      Sentry.captureException(error);
      console.error('[TicketSelectionForm] Failed to build DONATE display snapshot:', error);
      return;
    }

    data.mappedData = fieldMapper(data.fieldMappings, '', data.customInputsValues, []);

    // NEW: Use standardized color preparation
    const colorData = prepareColorsForPostMessage(brandColors);
    Object.assign(data, colorData);

    // Remove cart-specific fields (tickets don't use cart mode)
    delete data.cartItems;
    delete data.products;
    // Only delete data field if it's null/undefined (keep if it has content)
    if (data.data === null || data.data === undefined) {
      delete data.data;
    }

    // Get PostHog session ID for cross-app analytics continuity
    // Using official PostHog API: get_session_id()
    const posthogSessionId = posthog?.get_session_id?.() || null;

    // Track form submission before sending to checkout
    // CRITICAL: Wrap in try-catch to ensure analytics never blocks user flow
    try {
      trackForm(FORM_EVENTS.FORM_SUBMITTED, {
        form_id: configData?.formID,
        form_type: FORM_TYPES.TICKET,
        amount: amt || 0,
        frequency: 'ONE_TIME',
        is_recurring: false,
        is_hosted_page: hostedPage,
        tickets_selected: allAttendees?.length || 0,
        ticket_types_count: Object.keys(map || {}).length,
      });
    } catch (error) {
      console.error('[TicketSelectionForm] Analytics tracking failed (non-blocking):', error);
    }

    // ⚠️ SECURITY TRADEOFF: targetOrigin set to '*' (any origin)
    //
    // WHY: This widget is designed to be embedded on ANY nonprofit website via iframe.
    // We cannot know the parent domain in advance (could be thousands of different nonprofits).
    //
    // MITIGATION:
    // - Only sending non-sensitive donation data (amounts, frequencies, fund selections)
    // - No PII, payment data, or authentication tokens are sent via postMessage
    // - Actual payment processing happens in secure checkout flow with proper origin validation
    // - The HOSTED_SCRIPT on parent page validates and sanitizes all received data
    //
    // ALTERNATIVE CONSIDERED: window.location.ancestorOrigins[0]
    // - Only works in Chromium browsers (not Firefox, Safari)
    // - Would break functionality for 30%+ of users
    // - Not a viable solution for production embeddable widgets

    // Forward URL prefill fields to checkout
    const urlOverrides = props.urlOverrides || {};
    [
      'firstname',
      'lastname',
      'organizationname',
      'email',
      'phone',
      'address',
      'address2',
      'city',
      'state',
      'zip',
      'country',
    ].forEach((f) => {
      if (urlOverrides[f]) data[`prefill_${f}`] = urlOverrides[f];
    });

    // Forward ci_* and gci_* (custom input) params to checkout for global custom inputs
    Object.entries(urlOverrides).forEach(([k, v]) => {
      if (k.startsWith('ci_') || k.startsWith('gci_')) data[k] = v;
    });

    try {
      attachDonateSnapshot(data, donateSnapshot, displayGiftAmount);
    } catch (error) {
      setTicketSelectionError('Something went wrong. Please refresh and try again.');
      Sentry.captureException(error);
      console.error('[TicketSelectionForm] Failed to attach DONATE display snapshot:', error);
      return;
    }

    setIsSubmitting(true);

    // CON-86 / PE-2140: the embed script on the host page posts
    // CHECKOUT_PRELOAD_LOST back to THIS iframe when it cannot hand the tap to
    // the checkout. Armed here, beside the DONATE, because that is the only
    // point at which a tap exists to lose. Idempotent — at most one listener
    // per document however many times the donor submits.
    ensureLostCheckoutTapListener({ sender: 'EVENT' });

    // CON-63 mark 2 — checkout_requested, stamped where the donor pressed the
    // button, on whichever surface this form is rendered by. NOT stamped at the
    // hosted page's `DONATE` re-broadcast, which the plan proposed: that handler
    // returns early whenever the message already carries a `posthogSessionId`,
    // and this form always supplies one from the same PostHog instance — measured,
    // the mark never arrived from there. Capture only; the message is untouched.
    markJourney(JOURNEY_MARKS.CHECKOUT_REQUESTED);

    safePostMessageWithSelfFallback(window.parent, {
      action: 'DONATE',
      entity: 'CHARITYSTACK',
      sender: 'EVENT',
      hostedPage: hostedPage,
      receiver: 'HOSTED_SCRIPT',
      donationData: JSON.stringify(data),
      posthogSessionId: posthogSessionId,
    });
    console.log(data);

    // Reset form after standard delay
    scheduleFormReset(resetFormState, keepResetTimeoutId);
    // }, window.location.ancestorOrigins[0]);
  };

  useEffect(() => {
    if (!ticket2.ticket || !Array.isArray(ticket2.ticket)) {
      setAmt(0);
      setMapping([]);
      return;
    }

    const projection = projectBuyerTicketPurchaseSafely({
      ticketContractVersion: configData.ticketContractVersion,
      tickets: ticket2.ticket,
    });
    if (!projection.purchase) {
      setAmt(0);
      setMapping([]);
      setTicketSelectionError(projection.error);
      return;
    }
    const purchase = projection.purchase;
    setAmt(purchase.amount);
    setMapping(purchase.ticketMapping);
    setTicketSelectionError((current) =>
      current === TICKET_AVAILABILITY_CHANGED_MESSAGE ? '' : current,
    );
  }, [ticket2, configData.ticketContractVersion]);

  const displayOrder = {
    tickets: ticket2.ticket || [],
    purchasedGroups: (ticket2.ticket || []).flatMap(ticket => ticket.selectedGroups || []),
  };

  const fixedFormElementHeight = getStandardizedFormHeight({ formDisplayMode });
  // The drawer keeps its height floor (CON-529 F9 changes only the card): its height lock measures against it.
  const fixedFormElementStyles = getStandardizedFormElementStyles({ formDisplayMode, floor: isDrawerOpen });
  const fixedFormBodyStyles = getStandardizedFormBodyStyles({ formDisplayMode });
  const transitionClassName = getPageTransitionClasses(
    isPageActive,
    pageTransitionDirection,
    formDisplayMode,
  );
  const transitionWrapperStyle = {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 auto',
    minHeight: 0,
    width: '100%',
  };

  // Units picked across every tier, for the gift summary line.
  const selectedTicketCount = Array.isArray(ticket2.ticket)
    ? ticket2.ticket.reduce((count, tier) => count + (Number(tier.soldNow) || 0), 0)
    : 0;

  // FIXED: Use configData.active instead of local active state (like other forms)
  if (!props.previewMode && configData.active === false) {
    return <FormUnavailable />;
  } else if (!ticket2.ticket || !Array.isArray(ticket2.ticket)) {
    // Use the same loading skeleton as EmbedForm for consistent styling
    return <div className="text-center text-[color:var(--cs-text-muted)]">Loading...</div>;
  } else {
    return (
      <div
        data-name={elementName}
        id={`${elementID}-CS-ticket-form`}
        className={`page-container ${formDisplayMode === 'dynamic' && !isDrawerOpen && !fixedFormElementHeight ? 'dynamic-page-slide-x' : ''}`}
        style={{
          minHeight: isDrawerOpen ? '100%' : 'fit-content',
          height: isDrawerOpen ? '100%' : 'auto',
          overflow: 'visible',
        }}
      >
        <div
          className={`page ${page === 'START_PAGE' ? 'active' : ''} ${!isDrawerOpen && !isHostedPage && formDisplayMode !== 'overlay' ? 'container' : ''} ${!isDrawerOpen && formDisplayMode !== 'overlay' ? 'mx-auto' : ''} h-full items-center ${isDrawerOpen ? 'w-full px-1' : isHostedPage ? 'max-w-lg px-3' : formDisplayMode === 'overlay' ? 'w-full' : 'max-w-md'} iframe-content-container page-transition-container`}
          id={`${elementID}-CS-ticket-form-container`}
        >
          {/* Shadow Spacing Wrapper */}
          <div
            className={`${
              isHostedPage && !isDrawerOpen
                ? 'hosted-spacing-wrapper'
                : !isHostedPage && formDisplayMode !== 'overlay' && !isDrawerOpen
                  ? 'dynamic-spacing-wrapper'
                  : ''
            } ${formDisplayMode === 'overlay' ? 'flex-1 flex flex-col' : ''}`}
            style={{
              ...(!isHostedPage || isDrawerOpen
                ? {
                    padding: isDrawerOpen
                      ? '16px 0'
                      : formDisplayMode === 'overlay'
                        ? '0'
                        : '24px 0',
                  }
                : {}),
              margin: '0',
              height: isDrawerOpen ? '100%' : 'fit-content',
              boxSizing: 'border-box',
            }}
          >
            {/* Frosted Glass Background Layer */}
            <div
              className={formCardClass({ isDrawerOpen, isHostedPage, isOverlay: formDisplayMode === 'overlay', isEvent: true })}
              style={{
                display: 'flex',
                flexDirection: 'column',
                height: isDrawerOpen ? '100%' : fixedFormElementHeight || 'auto',
                ...fixedFormElementStyles,
                ...(isDrawerOpen && {
                  margin: '0',
                  padding: '10px',
                  borderRadius: '0',
                }),
              }}
            >
              {!isDrawerOpen && (
                <FormTitleStrip
                  id={`${elementID}-CS-ticket-form-header`}
                  // Back to the event's detail page, like every form's first step (Util/detailPageBack).
                  onBack={getDetailPageBack({
                    isHostedPage,
                    hostedPagePreview,
                    enableEmbedForm,
                    formDisplayMode,
                    setParentPage,
                  })}
                >
                  Get tickets
                </FormTitleStrip>
              )}

              <div
                className={`flex flex-col flex-1 ${
                  isDrawerOpen ? 'h-full rounded-none justify-center bg-white' : 'cs-form-card-body'
                }`}
                id={`${elementID}-CS-ticket-form-body`}
                style={{
                  height: isDrawerOpen ? '100%' : fixedFormElementHeight ? 'auto' : 'auto',
                  minHeight: 0,
                  ...(isDrawerOpen ? { padding: '0' } : {}),
                  ...fixedFormBodyStyles,
                }}
              >
                <div
                  className={`flex flex-col cs-form-sections ${transitionClassName}`.trim()}
                  style={transitionWrapperStyle}
                >
                  <div className="ticket-list-scroll-wrapper">
                    <div
                      className={`flex w-full flex-col gap-[var(--cs-space-2)] ${ticketSelectionError ? 'rounded-[var(--cs-control-radius)] border border-[color:var(--cs-error-color)] p-2' : ''}`}
                    >
                      {ticket2.ticket &&
                        Array.isArray(ticket2.ticket) &&
                        ticket2.ticket.map((t1, index) => {
                          const isV2 = configData.ticketContractVersion === 2;
                          const isGroup =
                            t1.ticketType === 'GROUP' ||
                            t1.type === 'GROUP' ||
                            Number(t1.seats) > 1;
                          const isMinimum = isV2 && t1.groupSizeMode === 'MINIMUM';
                          const selectedUnits = Number(t1.soldNow) || 0;
                          const availability = getBuyerTicketAvailability(
                            t1,
                            configData.ticketContractVersion,
                          );
                          const unavailable = t1.archived === true || t1.ticketSalesPaused === true;
                          const selectedSeatTotal = (t1.selectedGroups ?? []).reduce(
                            (sum, group) => sum + group.seatCount,
                            0,
                          );
                          const remainingSeatCapacity = Math.max(
                            Number(t1.seatCapacity || 0) - Number(t1.seatsSold || 0),
                            0,
                          );
                          const priceLabel =
                            t1.price == 0
                              ? 'Free'
                              : `${formatAmountLabel(ticketUnitDisplay(t1.price, ctx, rounded), ctx, v => `$${formatAmount(v)}`)}${
                                  isV2 && isGroup && t1.pricingMode === 'PER_SEAT'
                                    ? ' per seat'
                                    : ''
                                }`;
                          const seatLabel = isMinimum
                            ? `${t1.minimumSeats}${
                                t1.maximumSeats == null ? '+' : `–${t1.maximumSeats}`
                              } seats`
                            : isGroup
                              ? `${t1.groupSize ?? t1.seats} seats`
                              : '1 seat';

                          const updateUnit = (action) => {
                            const newTickets = [...ticket2.ticket];
                            const createAttendee = createAttendeeFactory(
                              t1,
                              props.urlOverrides || {},
                            );
                            newTickets[index] =
                              action === 'plus'
                                ? addBuyerTicketUnit({
                                    ticket: t1,
                                    ticketContractVersion: configData.ticketContractVersion,
                                    purchasedGroupID: isV2 ? generatePurchasedGroupID() : undefined,
                                    createAttendee,
                                  })
                                : removeBuyerTicketUnit({
                                    ticket: t1,
                                    ticketContractVersion: configData.ticketContractVersion,
                                  });
                            applyTicketSelections(newTickets);
                            if (newTickets.flatMap((ticket) => ticket.attendees ?? []).length > 0) {
                              setTicketSelectionError('');
                            }
                          };

                          const updateGroupSize = (group, seatCount) => {
                            const newTickets = [...ticket2.ticket];
                            newTickets[index] = resizeBuyerTicketGroup({
                              ticket: t1,
                              ticketContractVersion: configData.ticketContractVersion,
                              purchasedGroupID: group.purchasedGroupID,
                              seatCount,
                              createAttendee: createAttendeeFactory(t1, props.urlOverrides || {}),
                            });
                            applyTicketSelections(newTickets);
                          };

                          return (
                            <div
                              key={t1.id || `ticket-${index}`}
                              // Donor redesign (CON-516, SPEC §C selected tile; Paper 4K0-0):
                              // a ticket row is a choice tile — white + 1px edge, and the
                              // selected recipe (2px accent edge on the brand tint) once
                              // the donor has picked at least one. Sold out / paused rows
                              // sit at the one disabled opacity.
                              className={`relative flex flex-col items-start overflow-hidden box-border rounded-[var(--cs-control-radius)] transition-colors duration-[var(--cs-motion-instant)] ${
                                selectedUnits > 0
                                  ? TILE_SELECTED_COLOR_CLASS
                                  : 'border border-[color:var(--cs-border-control)] bg-[color:var(--cs-surface-0)] shadow-[shadow:var(--cs-control-shadow)]'
                              }`}
                            >
                              {/* Paper 4K0-0 ticket tier: name · price over the seat line,
                                  stepper on the right. A sold-out / paused tier keeps its
                                  edge and dims its contents to the one disabled level. */}
                              <div className={`flex w-full flex-row items-center gap-[var(--cs-space-3)] ${selectedUnits > 0 ? 'px-[calc(var(--cs-space-4)-1px)] py-[calc(var(--cs-space-3)-1px)]' : 'px-[var(--cs-space-4)] py-[var(--cs-space-3)]'}`}>
                                <div className={`flex min-w-0 flex-1 flex-col gap-[var(--cs-space-1)] ${availability <= 0 || unavailable ? 'opacity-[var(--cs-opacity-disabled)]' : ''}`}>
                                  <div className="flex min-w-0 flex-wrap items-baseline gap-x-[var(--cs-space-1)] font-sans text-[length:var(--cs-text-base)] leading-[var(--cs-leading-24)] text-[color:var(--cs-text-primary)] [font-feature-settings:'liga'_off,'clig'_off]">
                                    <span className="font-medium">{t1.name}</span>
                                    <span className="font-semibold">· {priceLabel}</span>
                                  </div>
                                  <div className="flex flex-wrap items-center gap-[var(--cs-space-2)] font-sans text-[length:var(--cs-text-sm)] leading-[var(--cs-leading-20)] font-normal text-[color:var(--cs-text-secondary)] [font-feature-settings:'liga'_off,'clig'_off]">
                                    <span>
                                      {seatLabel}
                                      {!unavailable && availability > 0 ? ` · ${availability} left` : ''}
                                    </span>
                                    {(unavailable || availability <= 0) && (
                                      <span className="rounded-[var(--cs-radius-full)] bg-[color:var(--cs-surface-sunken)] px-[var(--cs-space-2)] py-[calc(var(--cs-space-1)/2)] font-sans text-[length:var(--cs-text-2xs-plus)] leading-[var(--cs-leading-14)] font-semibold text-[color:var(--cs-text-tertiary)]">
                                        {t1.archived
                                          ? 'Archived'
                                          : t1.ticketSalesPaused
                                            ? 'Sales paused'
                                            : 'Sold out'}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                {/* Quantity controls */}
                                <div className={`shrink-0 ${STEPPER_GROUP_CLASS}`}>
                                  <button
                                    className={stepperButtonClass('minus')}
                                    onClick={() => updateUnit('minus')}
                                    type="button"
                                    // A11-003: an icon-only stepper needs a name, like the group-seat steppers below.
                                    aria-label={`Decrease ${t1.name} quantity`}
                                    disabled={selectedUnits <= 0}
                                  >
                                    <MinusIcon aria-hidden="true" className={STEPPER_ICON_CLASS} strokeWidth={1.75} />
                                  </button>

                                  <span className={STEPPER_COUNT_CLASS}>
                                    {selectedUnits}
                                  </span>

                                  <button
                                    className={stepperButtonClass('plus')}
                                    onClick={() => updateUnit('plus')}
                                    type="button"
                                    // A11-003: an icon-only stepper needs a name, like the group-seat steppers below.
                                    aria-label={`Increase ${t1.name} quantity`}
                                    disabled={availability <= 0 || unavailable}
                                  >
                                    <PlusIcon aria-hidden="true" className={STEPPER_ICON_CLASS} strokeWidth={1.75} />
                                  </button>
                                </div>
                              </div>
                              {isMinimum && (t1.selectedGroups ?? []).length > 0 && (
                                <div className="w-full space-y-2 border-t border-[color:var(--cs-border-control-subtle)] px-[var(--cs-space-4)] pb-[var(--cs-space-3)] pt-[var(--cs-space-2)]">
                                  {(t1.selectedGroups ?? []).map((group, groupIndex) => {
                                    const configuredMaximum =
                                      t1.maximumSeats == null
                                        ? Number(t1.seatCapacity)
                                        : Number(t1.maximumSeats);
                                    return (
                                      <div
                                        key={group.purchasedGroupID}
                                        className="flex items-center justify-between font-sans text-[length:var(--cs-text-sm)] leading-[var(--cs-leading-20)] text-[color:var(--cs-text-secondary)]"
                                      >
                                        <span>Group {groupIndex + 1} seats</span>
                                        <div className={STEPPER_GROUP_CLASS}>
                                          <button
                                            type="button"
                                            aria-label={`Decrease seats for group ${groupIndex + 1}`}
                                            onClick={() =>
                                              updateGroupSize(group, group.seatCount - 1)
                                            }
                                            disabled={group.seatCount <= Number(t1.minimumSeats)}
                                            className={stepperButtonClass('minus')}
                                          >
                                            <MinusIcon aria-hidden="true" className={STEPPER_ICON_CLASS} strokeWidth={1.75} />
                                          </button>
                                          <span className={STEPPER_COUNT_CLASS}>
                                            {group.seatCount}
                                          </span>
                                          <button
                                            type="button"
                                            aria-label={`Increase seats for group ${groupIndex + 1}`}
                                            onClick={() =>
                                              updateGroupSize(group, group.seatCount + 1)
                                            }
                                            disabled={
                                              group.seatCount >= configuredMaximum ||
                                              selectedSeatTotal >= remainingSeatCapacity
                                            }
                                            className={stepperButtonClass('plus')}
                                          >
                                            <PlusIcon aria-hidden="true" className={STEPPER_ICON_CLASS} strokeWidth={1.75} />
                                          </button>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          );
                        })}
                    </div>
                  </div>
                  <AnimatedError error={ticketSelectionError} />

                  <GiftSummaryRow
                    count={selectedTicketCount}
                    singular="ticket"
                    plural="tickets"
                    amount={ctx.converted ? ticketDisplayTotal(Array.isArray(map) ? map : [], amt, ctx, rounded, displayOrder) : amt}
                    basis={ctx.converted ? "display" : "usd"}
                    elementID={elementID}
                    pickerElementID={elementID}
                  />

                  <FormActionButtons
                    paymentsUnavailable={!props.previewMode && props.data?.paymentsEnabled === false && amt > 0}
                    brandColors={brandColors}
                    primaryColor={primaryColor}
                    elementID={elementID}
                    onClick={handleButtonClick}
                    formType="EVENT"
                    amount={amt}
                    ticketCount={selectedTicketCount}
                    isHostedPage={isHostedPage}
                    setParentPage={setParentPage}
                    hostedPagePreview={hostedPagePreview}
                    enableEmbedForm={enableEmbedForm}
                    formDisplayMode={formDisplayMode}
                    isSubmitting={isSubmitting}
                  />
                </div>
              </div>
            </div>{' '}
            {/* Close frosted background div */}
          </div>{' '}
          {/* Close shadow spacing wrapper */}
        </div>
      </div>
    );
  }
}

export default TicketSelectionForm;
